'use client'

/* ============================================================
   RR Design System — Accent (brand) color customization

   Lets a user retint the app's brand color at runtime. The
   design system drives `--primary` and `--ring` from OKLCH CSS
   custom properties (see tokens/colors.css), so changing the
   brand is just overriding those two vars on <html>.

   IMPORTANT — the stored format is OKLCH channels ("L C H"), not
   HSL. The Tailwind preset wraps every core token as
   `oklch(var(--primary) / ...)`, so writing an HSL triple here
   produces `oklch(221 83% 53%)` — invalid CSS that the browser
   drops silently, leaving `bg-primary` transparent with
   near-white `text-primary-foreground` on top (i.e. invisible
   buttons and nav pills). Values written to the token MUST be
   OKLCH channels.

   - Persisted to localStorage (per device), like ThemeProvider.
   - Stored as OKLCH channels:  "L C H"  (the token format).
   - Legacy HSL triples from before the OKLCH token migration are
     converted on read and rewritten, so a device keeps the accent
     its user picked instead of silently losing the brand color.
   - `useAccentColor()` exposes the current value + a setter and
     re-applies on mount so a reload keeps the choice.
   - `ACCENT_PRESETS` is a small curated palette; apps can also
     pass any custom hex via `setAccentFromHex`.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'rr-accent'

export interface AccentPreset {
  id: string
  label: string
  /** OKLCH token string: "L C H". */
  oklch: string
  /** Hex for the swatch / native color input. */
  hex: string
}

/** Curated brand tints. `oklch` values match the token format in colors.css. */
export const ACCENT_PRESETS: AccentPreset[] = [
  { id: 'blue',   label: 'Blue',   oklch: '0.546 0.215 262.9', hex: '#2563eb' },
  { id: 'indigo', label: 'Indigo', oklch: '0.511 0.230 277.0', hex: '#4f46e5' },
  { id: 'violet', label: 'Violet', oklch: '0.541 0.247 293.0', hex: '#7c3aed' },
  { id: 'teal',   label: 'Teal',   oklch: '0.600 0.104 184.7', hex: '#0d9488' },
  { id: 'green',  label: 'Green',  oklch: '0.723 0.192 149.6', hex: '#22c55e' },
  { id: 'amber',  label: 'Amber',  oklch: '0.769 0.165 70.1',  hex: '#f59e0b' },
  { id: 'rose',   label: 'Rose',   oklch: '0.586 0.222 17.6',  hex: '#e11d48' },
]

/** sRGB channel (0..1) -> linear-light. */
function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/** Linear-light sRGB -> OKLCH channel string "L C H". */
function linearRgbToOklchToken(r: number, g: number, b: number): string {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b
  const cbrt = (x: number) => (x >= 0 ? Math.cbrt(x) : -Math.cbrt(-x))
  const l_ = cbrt(l)
  const m_ = cbrt(m)
  const s_ = cbrt(s)
  const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_
  const A = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
  const C = Math.hypot(A, B)
  const H = ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360
  return `${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)}`
}

/** Convert a #rrggbb hex to the "L C H" OKLCH token string. */
export function hexToAccentToken(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const int = parseInt(m[1], 16)
  const r = srgbToLinear(((int >> 16) & 255) / 255)
  const g = srgbToLinear(((int >> 8) & 255) / 255)
  const b = srgbToLinear((int & 255) / 255)
  return linearRgbToOklchToken(r, g, b)
}

/**
 * Convert a legacy "H S% L%" triple (the pre-OKLCH storage format) to OKLCH
 * channels. Returns null if the string isn't an HSL triple.
 */
export function hslTokenToAccentToken(hsl: string): string | null {
  const m = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(hsl)
  if (!m) return null
  const h = parseFloat(m[1]) / 360
  const s = parseFloat(m[2]) / 100
  const l = parseFloat(m[3]) / 100
  const hue = (p: number, q: number, t: number) => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }
  let r = l
  let g = l
  let b = l
  if (s !== 0) {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue(p, q, h + 1 / 3)
    g = hue(p, q, h)
    b = hue(p, q, h - 1 / 3)
  }
  return linearRgbToOklchToken(srgbToLinear(r), srgbToLinear(g), srgbToLinear(b))
}

/** True when a stored value is a legacy HSL triple rather than OKLCH channels. */
const isLegacyHslToken = (value: string) => value.includes('%')

/**
 * The retired "Slate" accent ("215 25% 27%"). It sat too close to the
 * dark-theme surface colors to read as an accent, so it was removed from
 * ACCENT_PRESETS. Devices that previously selected it still have it in
 * localStorage — drop it on read so they fall back to the design-system
 * default instead of keeping the low-contrast override.
 */
const RETIRED_ACCENTS = new Set(['215 25% 27%'])

/**
 * Read the persisted accent as OKLCH channels, migrating (and rewriting) a
 * legacy HSL triple in place. Exported so ThemeProvider can re-apply the
 * accent on first mount without duplicating the migration.
 */
export function readStoredAccent(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    if (RETIRED_ACCENTS.has(stored.trim())) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (!isLegacyHslToken(stored)) return stored
    const migrated = hslTokenToAccentToken(stored)
    if (!migrated) {
      // Unparseable legacy value — drop it rather than write invalid CSS.
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    localStorage.setItem(STORAGE_KEY, migrated)
    return migrated
  } catch {
    return null
  }
}

/** Apply (or clear) the brand override on the document root. */
export function applyAccent(token: string | null) {
  const root = document.documentElement
  if (token) {
    root.style.setProperty('--primary', token)
    root.style.setProperty('--ring', token)
  } else {
    root.style.removeProperty('--primary')
    root.style.removeProperty('--ring')
  }
}

export function useAccentColor() {
  // null = "use the design-system default" (no override).
  const [accent, setAccent] = useState<string | null>(null)

  // Re-apply the stored override on mount (survives reloads).
  useEffect(() => {
    const stored = readStoredAccent()
    setAccent(stored)
    applyAccent(stored)
  }, [])

  const set = useCallback((token: string | null) => {
    setAccent(token)
    applyAccent(token)
    try {
      if (token) localStorage.setItem(STORAGE_KEY, token)
      else localStorage.removeItem(STORAGE_KEY)
    } catch { /* private mode */ }
  }, [])

  const setFromHex = useCallback((hex: string) => {
    const token = hexToAccentToken(hex)
    if (token) set(token)
  }, [set])

  const reset = useCallback(() => set(null), [set])

  return { accent, setAccent: set, setAccentFromHex: setFromHex, reset }
}
